import { CircularProgress, FormControlLabel, Stack, Switch, Tooltip } from '@mui/material';
import { type FeatureFlagKey } from '@nohara-dxdev/mt-common';
import { permission } from '@nohara-dxdev/oidc-core';
import { useCanAccess, useScopeIds } from '@nohara-dxdev/react-oidc';
import { useTranslation } from 'react-i18next';

import { useToggleOrgFeatureFlag } from '../api/toggle-org-feature-flag';

export interface FeatureFlagOrgToggleProps {
  flagKey: FeatureFlagKey;
  orgEnabled: boolean;
}

export const FeatureFlagOrgToggle = ({ flagKey, orgEnabled }: FeatureFlagOrgToggleProps) => {
  const { t } = useTranslation();
  const subject = useScopeIds();
  const toggle = useToggleOrgFeatureFlag();

  const { canAccess: canEnable } = useCanAccess({
    requireAll: [permission.featureFlag.update, permission.aiAgent.enable],
    context: subject,
  });
  const { canAccess: canDisable } = useCanAccess({
    requireAll: [permission.featureFlag.update, permission.aiAgent.disable],
    context: subject,
  });

  let switchDisabled = false;
  let tooltipKey = '';
  if (orgEnabled && !canDisable) {
    switchDisabled = true;
    tooltipKey = 'featureFlags.tooltips.cannotDisable';
  } else if (!orgEnabled && !canEnable) {
    switchDisabled = true;
    tooltipKey = 'featureFlags.tooltips.cannotEnable';
  }

  const isPending = toggle.isPending;

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Tooltip title={switchDisabled ? t(tooltipKey) : ''}>
        <span>
          <FormControlLabel
            control={
              <Switch
                checked={orgEnabled}
                disabled={switchDisabled || isPending}
                onChange={(_, next) => {
                  toggle.mutate({ flagKey, enabled: next });
                }}
                slotProps={{
                  input: {
                    role: 'switch',
                    'aria-label': t(`featureFlags.flags.${flagKey}.label`),
                  },
                }}
              />
            }
            label={t(`featureFlags.flags.${flagKey}.label`)}
          />
        </span>
      </Tooltip>
      {isPending && <CircularProgress size={16} aria-label={t('featureFlags.toggle.pending')} />}
    </Stack>
  );
};
